import { getParentToolUseId } from '../utils/journey-attribution';
import {
  latestUnansweredQuestionId as findLatestUnansweredQuestionId,
  isQuestionAnswerMessage,
} from '../utils/unanswered-question';
import type { ChatMessage, UserMessage } from './use-chat-ws';

type Options = {
  messages: ChatMessage[];
};

const useChatMessages = ({ messages }: Options) => {
  // Anchor the TodoWrite at its *first* occurrence — the position where the agent first laid
  // out the plan. Subsequent TodoWrite updates are filtered out; their content is surfaced via
  // `latestTodos` so the inline list updates in place without jumping positions.
  const firstTodoIndex = messages.findIndex(m => m.type === 'tool_use' && m.tool === 'TodoWrite');
  const latestTodos = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.type === 'tool_use' && m.tool === 'TodoWrite') {
        return (
          (m.input.todos as
            | { content: string; status: 'pending' | 'in_progress' | 'completed'; activeForm?: string }[]
            | undefined) ?? []
        );
      }
    }
    return [] as { content: string; status: 'pending' | 'in_progress' | 'completed'; activeForm?: string }[];
  })();

  const toolResults = (() => {
    const map: Record<string, string> = {};
    for (const msg of messages) {
      if (msg.type === 'tool_result') {
        map[msg.tool_use_id] = msg.content;
      }
    }
    return map;
  })();

  // Parallel map of which tool_use ids errored. Lookup is `!!toolErrors[id]` so a
  // missing/false entry both mean "did not error". Backend `is_error` only travels
  // on the history-load path today — the live `WsToolResultEvent` does not include
  // it (see `frontend/packages/request-types/src/percy.ts::WsToolResultEvent`).
  // Until that's added, in-session errors won't be detected here; reload surfaces
  // them via persisted history.
  const toolErrors = (() => {
    const map: Record<string, boolean> = {};
    for (const msg of messages) {
      if (msg.type === 'tool_result' && msg.is_error) {
        map[msg.tool_use_id] = true;
      }
    }
    return map;
  })();

  const approvalToolResults = (() => {
    const map: Record<string, string | undefined> = {};
    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i];
      if (msg.type !== 'approval_required' || !msg.tool_name) continue;
      // Find the tool_use that follows this approval (the one actually triggered by it)
      const toolUse = messages.slice(i + 1).find(m => m.type === 'tool_use' && m.tool === msg.tool_name);
      if (toolUse && toolUse.type === 'tool_use') {
        map[msg.approval_id] = toolResults[toolUse.id];
      }
    }
    return map;
  })();

  const questionAnswerMessages: Record<string, UserMessage> = Object.fromEntries(
    messages.filter(isQuestionAnswerMessage).map(m => [m.question_id, m]),
  );

  const visibleMessages = messages
    .map((msg, originalIndex) => ({ msg, originalIndex }))
    .filter(({ msg, originalIndex: i }) => {
      if (msg.type === 'tool_result') return false;
      // `turn_complete` is a lifecycle marker — ChatMessage returns null for it; leaving
      // it in visibleMessages just creates an empty wrapper div + stray gap.
      if (msg.type === 'turn_complete') return false;
      if (msg.type === 'tool_use' && msg.tool === 'TodoWrite' && i !== firstTodoIndex) return false;
      // Question-answer messages are NOT filtered: they render as a Q/A bubble at their
      // real chronological position (where the user answered), not folded into the
      // question's earlier slot.
      if (msg.type === 'error' && /timed out|handshake|reconnect|connection/i.test(msg.message)) return false;
      return true;
    });

  const latestUnansweredQuestionId = findLatestUnansweredQuestionId(messages);

  /** Approval IDs that have already been resolved (agent continued after them in the history). */
  const resolvedApprovalIds = (() => {
    const resolved = new Set<string>();
    let seenAgentActivity = false;
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.type === 'approval_required') {
        if (seenAgentActivity) resolved.add(m.approval_id);
      } else if (m.type === 'text' || m.type === 'tool_use' || m.type === 'tool_result') {
        // A background journey keeps emitting while a main-thread approval is
        // parked — its attributed frames must not read as "the agent continued".
        if (!getParentToolUseId(m)) seenAgentActivity = true;
      } else if (m.type === 'user') {
        seenAgentActivity = false;
      }
    }
    return resolved;
  })();

  return {
    toolResults,
    toolErrors,
    approvalToolResults,
    questionAnswerMessages,
    visibleMessages,
    latestUnansweredQuestionId,
    latestTodos,
    resolvedApprovalIds,
  };
};

export default useChatMessages;
